'use client';

// UrlInputField - URL input component for extracting web page content

import { useState, useCallback, type KeyboardEvent } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import LinkIcon from '@mui/icons-material/Link';
import { Input, Button } from '@boty/ui';
import { useTranslations } from 'next-intl';

// Regex for validating HTTP/HTTPS URLs with proper domain format
// Must have protocol + domain with at least one dot (e.g., example.com)
const URL_WITH_PROTOCOL_REGEX = /^https?:\/\/[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+/i;

// Regex to detect if user started typing a URL without protocol
const URL_WITHOUT_PROTOCOL_REGEX = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+/i;

/**
 * Normalizes a URL by adding https:// if no protocol is present
 */
function normalizeUrl(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) return trimmed;

  // Already has a protocol
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  // Looks like a domain without protocol - add https://
  if (URL_WITHOUT_PROTOCOL_REGEX.test(trimmed)) {
    return `https://${trimmed}`;
  }

  return trimmed;
}

export interface UrlInputFieldProps {
  /** Callback when a URL is submitted for extraction */
  onSubmit: (url: string) => void;
  /** @deprecated No longer used - input stays enabled during extraction */
  isLoading?: boolean;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Error message to display */
  error?: string | null;
}

/**
 * UrlInputField - Input field for entering URLs to extract content from
 *
 * Features:
 * - URL format validation (http:// or https://)
 * - Allows submitting multiple URLs in parallel
 * - Add button that appears when valid URL is entered
 * - Accessible with proper aria labels
 * - RTL support via CSS logical properties
 *
 * @example
 * ```tsx
 * <UrlInputField
 *   onSubmit={(url) => extractUrl(url)}
 *   error={errorMessage}
 * />
 * ```
 */
export function UrlInputField({
  onSubmit,
  // isLoading is deprecated but kept for backward compatibility
  isLoading: _isLoading = false,
  disabled = false,
  error = null,
}: UrlInputFieldProps) {
  const t = useTranslations('creator.urlExtraction');
  const [url, setUrl] = useState('');

  // Normalize the URL (add https:// if needed) and check if it's valid
  const normalizedUrl = normalizeUrl(url);
  const isValidUrl = URL_WITH_PROTOCOL_REGEX.test(normalizedUrl);

  // Handle URL submission
  const handleSubmit = useCallback(() => {
    if (isValidUrl && !disabled) {
      onSubmit(normalizedUrl);
      setUrl(''); // Clear input after submission
    }
  }, [normalizedUrl, isValidUrl, disabled, onSubmit]);

  // Handle Enter key press
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' && isValidUrl && !disabled) {
        event.preventDefault();
        handleSubmit();
      }
    },
    [isValidUrl, disabled, handleSubmit]
  );

  // Determine if there's an error to display
  const hasError = Boolean(error);

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1,
          alignItems: { xs: 'stretch', sm: 'flex-start' },
          width: '100%',
        }}
      >
        <Input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('input.placeholder')}
          disabled={disabled}
          error={hasError}
          helperText={error}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LinkIcon color={hasError ? 'error' : 'action'} />
              </InputAdornment>
            ),
          }}
          inputProps={{
            'aria-label': t('input.placeholder'),
            type: 'url',
            autoComplete: 'off',
            enterKeyHint: 'send',
          }}
          sx={{
            flex: 1,
            '& .MuiInputBase-input': {
              fontSize: { xs: '16px', md: 'inherit' }, // 16px prevents iOS zoom on focus
            },
          }}
        />
        <Button
          variant="contained"
          size="medium"
          onClick={handleSubmit}
          disabled={!isValidUrl || disabled}
          aria-label={t('input.addButton')}
          sx={{
            // Mobile: full-width button below input so it stays visible above the on-screen keyboard
            width: { xs: '100%', sm: 'auto' },
            minWidth: { sm: 80 },
            minHeight: 48, // Touch target minimum
            height: { xs: 48, sm: 56 }, // Match MUI TextField height on desktop
          }}
        >
          {t('input.addButton')}
        </Button>
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 0.5, display: 'block' }}
      >
        {t('input.supportedSources')}
      </Typography>
    </Box>
  );
}
